import { useMemo, useState } from 'react';
import {
  apiErrorText,
  apiLoadErrorText,
  useListMtoReceiptObjectsQuery,
  useResuggestMtoReceiptObjectsMutation,
  useUpdateMtoReceiptObjectMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { CheckLabel, Control, FleetChip, Notice, TextButton, typo } from '@entities/fleet';
import {
  MATERIAL_KIND_LABEL,
  formatDate,
  formatMoney0,
  type MaterialKind,
  type ObjectPatch,
  type ReceiptObject,
} from '@entities/mto/receipts';
import { CellSelect, EmptyCard, Table, TableBox, Toolbar, Totals } from './styles';

type Filter = 'year' | 'unmapped' | 'suggested' | 'all';

function targetValue(o: ReceiptObject): string {
  if (o.project_id) return `p:${o.project_id}`;
  if (o.econ_object_id) return `c:${o.econ_object_id}`;
  return '';
}

function targetPatch(value: string): ObjectPatch['target'] {
  if (value.startsWith('p:')) return { project_id: value.slice(2) };
  if (value.startsWith('c:')) return { econ_object_id: value.slice(2) };
  return {};
}

/**
 * Объекты прихода: как объект записан в книге → объект ОСНОВЫ или центр
 * затрат и вид материала. Так материал ложится в БДР: «БОВ (проект)» — БОВ,
 * «согласно проектной документации»; «Склад» — АУП, прочие. Программа
 * предлагает сопоставление по названию, экономист подтверждает или меняет.
 * Старые объекты — в архив: в БДР не идут и в «не сопоставленных» не висят.
 */
export function ReceiptObjects({
  year,
  canMap,
  onFlash,
}: {
  year: number;
  canMap: boolean;
  onFlash: (f: { tone: 'success' | 'danger'; text: string }) => void;
}) {
  const { data, isLoading, error, isFetching, refetch } = useListMtoReceiptObjectsQuery({ year });
  const [update, updateState] = useUpdateMtoReceiptObjectMutation();
  const [resuggest, resuggestState] = useResuggestMtoReceiptObjectsMutation();
  const [filter, setFilter] = useState<Filter>('year');
  const [query, setQuery] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const targets = data?.targets;
  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      items.filter((o) => {
        if (q && !o.name.toLowerCase().includes(q)) return false;
        const mapped = o.project_id !== null || o.econ_object_id !== null;
        switch (filter) {
          case 'year':
            return o.rows_year > 0;
          case 'unmapped':
            return !mapped && !o.is_archive;
          case 'suggested':
            return mapped && o.mapped_by_rule;
          default:
            return true;
        }
      }),
    [items, filter, q],
  );
  const counts = useMemo(() => {
    const yearItems = items.filter((o) => o.rows_year > 0);
    return {
      year: yearItems.length,
      unmapped: items.filter((o) => !o.project_id && !o.econ_object_id && !o.is_archive).length,
      unmappedYear: yearItems.filter((o) => !o.project_id && !o.econ_object_id && !o.is_archive),
      suggested: items.filter((o) => (o.project_id || o.econ_object_id) && o.mapped_by_rule).length,
    };
  }, [items]);
  const unmappedYearSum = counts.unmappedYear.reduce((s, o) => s + (Number(o.amount_year) || 0), 0);

  async function save(o: ReceiptObject, body: ObjectPatch, okText?: string) {
    setSavingId(o.id);
    try {
      await update({ id: o.id, body }).unwrap();
      if (okText) onFlash({ tone: 'success', text: okText });
    } catch (err) {
      onFlash({ tone: 'danger', text: apiErrorText(err, `Не удалось сохранить «${o.name}».`) });
    } finally {
      setSavingId(null);
    }
  }

  async function onResuggest() {
    try {
      const { changed } = await resuggest().unwrap();
      onFlash({
        tone: 'success',
        text:
          changed > 0
            ? `Программа предложила сопоставление для ${changed} написаний — проверьте и подтвердите.`
            : 'Новых подсказок нет: для оставшихся написаний подходящего объекта или подразделения не нашлось.',
      });
    } catch (err) {
      onFlash({ tone: 'danger', text: apiErrorText(err, 'Не удалось обновить подсказки.') });
    }
  }

  async function confirmAll() {
    const list = items.filter(
      (o) => (o.project_id || o.econ_object_id) && o.mapped_by_rule && o.rows_year > 0,
    );
    if (list.length === 0) return;
    if (!window.confirm(`Подтвердить предложенные сопоставления за ${year} год: ${list.length}?`))
      return;
    for (const o of list) {
      // По одному: у каждого — своя запись в журнале правок.
      await save(o, { confirm: true });
    }
    onFlash({ tone: 'success', text: `Подтверждено сопоставлений: ${list.length}.` });
  }

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Загрузка объектов прихода">
        <SkeletonLine $height={40} />
        <SkeletonLine $height={20} />
        <SkeletonLine $height={20} $width="70%" />
      </div>
    );
  }
  if (error !== undefined && !data) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">
          {apiLoadErrorText(error, 'Объекты прихода не загрузились.', 'Нет доступа к приходу ТМЦ.')}
        </span>
        <TextButton type="button" disabled={isFetching} onClick={() => void refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  if (items.length === 0) {
    return (
      <EmptyCard>
        <h3>Объектов прихода пока нет</h3>
        <p>
          {typo(
            'Они заводятся сами, когда загружают книгу или вносят строку: каждое написание объекта — отдельная запись.',
          )}
        </p>
      </EmptyCard>
    );
  }

  return (
    <>
      <Toolbar>
        <Select
          aria-label="Какие объекты показать"
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
        >
          <option value="year">
            С приходом за {year} год ({counts.year})
          </option>
          <option value="unmapped">Не сопоставлены ({counts.unmapped})</option>
          <option value="suggested">Предложены программой ({counts.suggested})</option>
          <option value="all">Все ({items.length})</option>
        </Select>
        <Control
          className="search"
          type="search"
          value={query}
          placeholder="Найти объект"
          aria-label="Поиск объекта прихода"
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="spacer" />
        {canMap && counts.unmapped > 0 && (
          <Button
            type="button"
            $variant="ghost"
            disabled={resuggestState.isLoading}
            title="Например, после того как завели новый объект или подразделение"
            onClick={() => void onResuggest()}
          >
            Предложить для несопоставленных
          </Button>
        )}
        {canMap && counts.suggested > 0 && (
          <Button
            type="button"
            $variant="secondary"
            disabled={updateState.isLoading}
            onClick={() => void confirmAll()}
          >
            Подтвердить предложенные за {year}
          </Button>
        )}
      </Toolbar>

      {counts.unmappedYear.length > 0 && (
        <Totals>
          <span>
            За {year} год не сопоставлено: <b>{counts.unmappedYear.length}</b> написаний на{' '}
            <b>{formatMoney0(unmappedYearSum)}</b> ₽ без НДС — в БДР они не попадут, пока их не
            сопоставят.
          </span>
        </Totals>
      )}

      {!canMap && (
        <Notice $tone="info" role="note">
          <span className="text">{typo('Только просмотр: объекты сопоставляет экономист.')}</span>
        </Notice>
      )}

      {visible.length === 0 ? (
        <EmptyCard>
          <h3>Ничего не нашлось</h3>
          <p>{typo('Под этот отбор объектов нет — выберите «Все».')}</p>
        </EmptyCard>
      ) : (
        <TableBox>
          <Table>
            <thead>
              <tr>
                <th scope="col">Как записано</th>
                <th scope="col" className="num">
                  {year}, ₽ без НДС
                </th>
                <th scope="col" className="num">
                  Всего, ₽
                </th>
                <th scope="col">Объект или подразделение</th>
                <th scope="col">Материал</th>
                <th scope="col">Состояние</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => {
                const busy = savingId === o.id;
                const mapped = o.project_id !== null || o.econ_object_id !== null;
                return (
                  <tr key={o.id}>
                    <td>
                      {o.name}
                      <div className="sub">
                        {o.rows_total.toLocaleString('ru-RU')} строк
                        {o.last_date && `, последняя ${formatDate(o.last_date)}`}
                      </div>
                    </td>
                    <td className="num">{o.rows_year > 0 ? formatMoney0(o.amount_year) : '—'}</td>
                    <td className="num">{formatMoney0(o.amount_total)}</td>
                    <td>
                      <CellSelect
                        $min={220}
                        aria-label={`Куда относится «${o.name}»`}
                        value={targetValue(o)}
                        disabled={!canMap || busy || !targets}
                        onChange={(e) => void save(o, { target: targetPatch(e.target.value) })}
                      >
                        <option value="">— не сопоставлен —</option>
                        {targets && targets.projects.length > 0 && (
                          <optgroup label="Объекты">
                            {targets.projects.map((p) => (
                              <option key={p.id} value={`p:${p.id}`}>
                                {p.code && p.code !== p.name ? `${p.name} (${p.code})` : p.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {targets && targets.cost_centers.length > 0 && (
                          <optgroup label="Подразделения (центры затрат)">
                            {targets.cost_centers.map((c) => (
                              <option key={c.id} value={`c:${c.id}`}>
                                {c.code}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </CellSelect>
                    </td>
                    <td>
                      <CellSelect
                        $min={150}
                        aria-label={`Вид материала «${o.name}»`}
                        value={o.material_kind}
                        disabled={!canMap || busy}
                        onChange={(e) =>
                          void save(o, { material_kind: e.target.value as MaterialKind })
                        }
                      >
                        <option value="project">{MATERIAL_KIND_LABEL.project}</option>
                        <option value="other">{MATERIAL_KIND_LABEL.other}</option>
                      </CellSelect>
                    </td>
                    <td className="nowrap">
                      {o.is_archive ? (
                        <FleetChip $tone="muted">архив</FleetChip>
                      ) : !mapped ? (
                        <FleetChip $tone="warning">не сопоставлен</FleetChip>
                      ) : o.mapped_by_rule ? (
                        <FleetChip $tone="info">предложено</FleetChip>
                      ) : (
                        <FleetChip $tone="success">подтверждено</FleetChip>
                      )}
                      {canMap && (
                        <div className="sub">
                          {mapped && o.mapped_by_rule && (
                            <TextButton
                              type="button"
                              disabled={busy}
                              onClick={() => void save(o, { confirm: true })}
                            >
                              Подтвердить
                            </TextButton>
                          )}
                          <CheckLabel>
                            <input
                              type="checkbox"
                              checked={o.is_archive}
                              disabled={busy}
                              onChange={(e) => void save(o, { is_archive: e.target.checked })}
                            />
                            Архив
                          </CheckLabel>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableBox>
      )}
    </>
  );
}
